import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Bell, Shield, ChevronRight, Palette, Sun, Moon, Monitor } from 'lucide-react';
import UserProfile from '../User/UserProfile';
import SimpleNotificationPreferences from '../Notifications/SimpleNotificationPreferences';
import SecurityTab from './SecurityTab';
import userProfileService from '../../services/userProfileService';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeOption = ({ value, label, icon: Icon, active, onClick }) => (
  <button
    onClick={() => onClick(value)}
    className={`
      relative flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200
      ${active
        ? 'border-primary-600 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
      }
    `}
  >
    <div className={`
      w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200
      ${active
        ? 'bg-primary-600 dark:bg-primary-500 text-white'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
      }
    `}>
      <Icon className="w-5 h-5" />
    </div>
    <span className={`text-sm font-medium transition-colors duration-200 ${active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'}`}>
      {label}
    </span>
    {active && (
      <motion.div
        layoutId="theme-indicator"
        className="absolute -top-1 -right-1 w-4 h-4 bg-primary-600 dark:bg-primary-400 rounded-full flex items-center justify-center"
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      >
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </motion.div>
    )}
  </button>
);

const AppearanceTab = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();

  return (
    <div className="p-6 space-y-8">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Theme</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Choose how FlexCOA looks to you</p>
      </div>

      <div className="grid grid-cols-3 gap-3 max-w-md">
        <ThemeOption value="system" label="System" icon={Monitor} active={theme === 'system'} onClick={setTheme} />
        <ThemeOption value="light" label="Light" icon={Sun} active={theme === 'light'} onClick={setTheme} />
        <ThemeOption value="dark" label="Dark" icon={Moon} active={theme === 'dark'} onClick={setTheme} />
      </div>

      {theme === 'system' && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs text-gray-500 dark:text-gray-400"
        >
          Currently using <span className="font-medium">{resolvedTheme}</span> based on your system settings
        </motion.p>
      )}
    </div>
  );
};

const ProfilePage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('account');
  const [userProfile, setUserProfile] = useState(null);

  const tabs = [
    { id: 'account', label: 'Account', icon: User, description: 'Personal information' },
    { id: 'security', label: 'Security', icon: Shield, description: 'Password & MFA' },
    { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Alert preferences' },
    { id: 'appearance', label: 'Appearance', icon: Palette, description: 'Theme & display' },
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    
    const validTabs = ['account', 'security', 'notifications', 'appearance'];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Load user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = await userProfileService.getUserProfile();
        setUserProfile(userData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your account, security, and preferences</p>
        </motion.div>

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {/* Tab Navigation */}
          <div className="flex flex-col lg:flex-row">
            {/* Tab Sidebar */}
            <div className="lg:w-56 bg-gray-50 dark:bg-gray-900 lg:border-r border-b lg:border-b-0 border-gray-200 dark:border-gray-700">
              <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        relative flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200
                        min-w-[120px] lg:min-w-0 lg:w-full
                        ${isActive 
                          ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm dark:shadow-gray-900/20 border border-gray-200 dark:border-gray-700' 
                          : 'text-gray-600 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'
                        }
                      `}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'}`} />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{tab.label}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 hidden lg:block">{tab.description}</span>
                      </div>
                      {isActive && (
                        <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500 hidden lg:block" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-[500px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="h-full"
                >
                  {activeTab === 'account' && (
                    <UserProfile embedded={true} />
                  )}

                  {activeTab === 'security' && (
                    <div className="p-6">
                      <SecurityTab userProfile={userProfile} embedded={true} />
                    </div>
                  )}

                  {activeTab === 'notifications' && (
                    <div className="p-6">
                      <SimpleNotificationPreferences embedded={true} />
                    </div>
                  )}
                  {activeTab === 'appearance' && (
                    <AppearanceTab />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
